import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { platformOrganizationPath } from '../../app/platform-context.js'
import { listOrganizations, type OrganizationListResult } from '../../shared/api/organizations.js'
import { formatDate } from '../../shared/utils/format.js'
import {
  usePlatformApiError,
  type PlatformApiErrorKind,
} from '../../app/usePlatformApiError.js'

const EMPTY: OrganizationListResult = {
  data: [],
  meta: { total: 0, page: 1, pageSize: 20, totalPages: 1 },
}

const PAGE_SIZE = 20

export default function OrganizationsPage() {
  const { t, i18n } = useTranslation()
  const handleApiError = usePlatformApiError()
  const [result, setResult] = useState<OrganizationListResult>(EMPTY)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page, setPage] = useState(1)
  const [reloadKey, setReloadKey] = useState(0)
  const [errorKind, setErrorKind] = useState<PlatformApiErrorKind>('unknown')

  useEffect(() => {
    let cancelled = false
    setStatus('loading')
    listOrganizations({ page, pageSize: PAGE_SIZE, search: search || undefined, status: statusFilter || undefined })
      .then(data => {
        if (cancelled) return
        setResult(data)
        setStatus('ready')
      })
      .catch((error: unknown) => {
        if (cancelled) return
        const kind = handleApiError(error)
        if (kind === null) return
        setErrorKind(kind)
        setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [page, search, statusFilter, reloadKey, handleApiError])

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  const hasFilters = search.length > 0 || statusFilter.length > 0

  return (
    <section className="page-section page-section--wide">
      <div className="page-header">
        <div>
          <h1 className="platform-page-title">{t('platform.organizations.title')}</h1>
          <p className="page-section-description">{t('platform.organizations.subtitle')}</p>
        </div>
      </div>

      <div className="platform-toolbar">
        <form className="platform-search" onSubmit={handleSearch}>
          <label className="platform-visually-hidden" htmlFor="organizationSearch">
            {t('platform.organizations.search')}
          </label>
          <input
            id="organizationSearch"
            type="search"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder={t('platform.organizations.searchPlaceholder')}
            maxLength={100}
            autoComplete="off"
          />
          <button type="submit" className="btn btn-primary">
            {t('platform.organizations.search')}
          </button>
        </form>

        <div className="platform-filter">
          <label htmlFor="organizationStatus">{t('platform.organizations.statusFilter')}</label>
          <select
            id="organizationStatus"
            value={statusFilter}
            onChange={event => {
              setStatusFilter(event.target.value)
              setPage(1)
            }}
          >
            <option value="">{t('platform.organizations.allStatuses')}</option>
            <option value="ACTIVE">{t('platform.organizationStatus.ACTIVE')}</option>
            <option value="SUSPENDED">{t('platform.organizationStatus.SUSPENDED')}</option>
            <option value="ARCHIVED">{t('platform.organizationStatus.ARCHIVED')}</option>
          </select>
        </div>
      </div>

      {status === 'loading' && (
        <div className="page-loading"><div className="spinner" /></div>
      )}

      {status === 'error' && (
        <div className="settings-card">
          <div className="form-error-banner" role="alert">
            {errorKind === 'unknown'
              ? t('platform.organizations.loadError')
              : t(`platform.errors.${errorKind}`)}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setReloadKey(current => current + 1)}
          >
            {t('error.retry')}
          </button>
        </div>
      )}

      {status === 'ready' && result.data.length === 0 && (
        <div className="empty-state">
          <h3>{hasFilters ? t('platform.organizations.noResults') : t('platform.organizations.empty')}</h3>
          <p>{hasFilters ? t('platform.organizations.noResultsHint') : t('platform.organizations.emptyHint')}</p>
        </div>
      )}

      {status === 'ready' && result.data.length > 0 && (
        <>
          <div className="platform-table-wrap">
            <table className="platform-table">
              <thead>
                <tr>
                  <th>{t('platform.organizations.columns.name')}</th>
                  <th>{t('platform.organizations.columns.slug')}</th>
                  <th>{t('platform.organizations.columns.status')}</th>
                  <th>{t('platform.organizations.columns.createdAt')}</th>
                  <th aria-label={t('platform.organizations.columns.actions')} />
                </tr>
              </thead>
              <tbody>
                {result.data.map(organization => (
                  <tr key={organization.id}>
                    <td>
                      <Link className="platform-table-link" to={platformOrganizationPath(organization.id)}>
                        {organization.name}
                      </Link>
                    </td>
                    <td><code className="platform-code">{organization.slug}</code></td>
                    <td>
                      <span className={`status-badge status-badge--${organization.status.toLowerCase()}`}>
                        {t(`platform.organizationStatus.${organization.status}`)}
                      </span>
                    </td>
                    <td>{formatDate(organization.createdAt, i18n.language)}</td>
                    <td className="platform-table-actions">
                      <Link className="btn btn-ghost" to={platformOrganizationPath(organization.id)}>
                        {t('platform.organizations.open')}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {result.meta.totalPages > 1 && (
            <div className="pagination">
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page <= 1}
                onClick={() => setPage(current => Math.max(1, current - 1))}
              >
                {t('platform.organizations.previous')}
              </button>
              <span className="pagination-label">
                {t('platform.organizations.pageOf', {
                  page: result.meta.page,
                  total: result.meta.totalPages,
                })}
              </span>
              <button
                type="button"
                className="btn btn-ghost"
                disabled={page >= result.meta.totalPages}
                onClick={() => setPage(current => current + 1)}
              >
                {t('platform.organizations.next')}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  )
}
